import { ITimeoutService } from 'angular';
import { ObjectId, User } from '../../../..';
import { FormQuestion } from '../../../../services/API/forms/forms';
import { Answer } from '../../../../services/API/reports/reports';
import { UsersService } from '../../../../services/API/users/users.service';
import { PubSubService } from '../../../../services/Utils/PubSub/pubsub.service';
import { FORM_QUESTION_ACTIONS } from '../../../form/components/form-question-actions/form-question-actions.constant';

export class QuestionUsersController implements ng.IController {
  selectedUsersIdsSet: Set<ObjectId>;
  selectedUsersIdsArray: ObjectId[];

  // Bindings
  question: FormQuestion;
  questionOptions: {
    maximum: number;
    required: number;
  };
  hasError: boolean;
  answers: Answer[];
  previousAnswers: Answer[];
  unconfirmedPrefilledQuestions: { ids: string[] };
  actionClickedFn: any;
  isDisabled: boolean;
  sfQuestionForm;

  constructor(
    private $timeout: ITimeoutService,
    private usersService: UsersService,
    private pubSubService: PubSubService
  ) {
    'ngInject';
  }

  $onInit() {
    this.actionClickedFn = this.actionClicked.bind(this);
    this.selectedUsersIdsSet = new Set();

    this.addAlreadySelectedUsers();

    this.selectedUsersIdsArray = Array.from(this.selectedUsersIdsSet);

    this.sortAnswers();
    this.registerOnConfirmPreviousAnswers();
  }

  /**
   * Only add already selected users in case of draft
   * In case of answers from a previous report, we consider a user wants
   * to start from scratch if they open the selection modal
   */
  addAlreadySelectedUsers(): void {
    const previousAnswersIds = new Set(
      this.previousAnswers.map((previousAnswer) => previousAnswer._id)
    );

    this.answers.forEach((answer) => {
      if (!previousAnswersIds.has(answer._id)) {
        this.selectedUsersIdsSet.add(answer.values[0].value);
      }
    });
  }

  registerOnConfirmPreviousAnswers(): void {
    this.pubSubService.subscribe(
      'CONFIRM_QUESTION_PREVIOUS_ANSWERS',
      (question: FormQuestion) => {
        if (question._id === this.question._id) {
          this.answers.forEach((answer) => {
            this.selectedUsersIdsSet.add(answer.values[0].value);
          });

          this.onUsersChanges();
        }
      }
    );
  }

  onUsersSelection(confirmedUsersIds: string[]) {
    this.removePreviousAnswers();
    this.checkForDeletedUsersIds(confirmedUsersIds);
    this.checkForAddedUsersIds(confirmedUsersIds).then(() => {
      this.onUsersChanges();
    });
  }

  checkForDeletedUsersIds(confirmedUsersIds: string[]): void {
    this.selectedUsersIdsSet.forEach((userId) => {
      if (!confirmedUsersIds.includes(userId)) {
        this.removeUser(userId);
      }
    });
  }

  checkForAddedUsersIds(confirmedUsersIds: string[]) {
    return this.getUsers(confirmedUsersIds).then((users) => {
      users.forEach((user) => {
        if (!this.selectedUsersIdsSet.has(user._id)) {
          this.addUser(user);
        }
      });
    });
  }

  removePreviousAnswers(): void {
    if (
      this.unconfirmedPrefilledQuestions?.ids?.includes(this.question._id) &&
      this.previousAnswers?.length
    ) {
      this.previousAnswers.forEach((answer) => {
        this.answers = this.sfQuestionForm.removeAnswer(
          answer._id,
          this.answers
        );
        this.selectedUsersIdsSet.delete(answer.values[0].value);
      });
    }
  }

  addUser(user: User): void {
    this.selectedUsersIdsSet.add(user._id);

    this.answers = this.sfQuestionForm.addAnswer(
      this.buildFieldAnswer(user),
      this.answers,
      { type: 'users' }
    );
  }

  removeUser(userId: string): void {
    this.selectedUsersIdsSet.delete(userId);

    const answer = this.answers.find(
      (answer) => answer.values[0].value === userId
    );

    if (answer) {
      this.answers = this.sfQuestionForm.removeAnswer(answer._id, this.answers);
    }

    this.onUsersChanges();
  }

  onUsersChanges(): void {
    this.$timeout(() => this.setDirty());
    this.selectedUsersIdsArray = Array.from(this.selectedUsersIdsSet);
    this.sortAnswers();
  }

  buildFieldAnswer(user: User) {
    return [
      {
        field_id: this.question.fields[0]._id,
        value: user._id,
      },
      {
        field_id: this.question.fields[1]._id,
        value: user.contents.firstName,
      },
      {
        field_id: this.question.fields[2]._id,
        value: user.contents.lastName,
      },
      {
        field_id: this.question.fields[3]._id,
        value: user.contents.email,
      },
    ];
  }

  actionClicked(
    action: typeof FORM_QUESTION_ACTIONS[keyof typeof FORM_QUESTION_ACTIONS]
  ): void {
    switch (action) {
      case FORM_QUESTION_ACTIONS.RESET:
        return this.resetAnswers();
      case FORM_QUESTION_ACTIONS.RESET_PREFILL:
        return this.resetToPrefillValue();
      default:
        return;
    }
  }

  setDirty(): void {
    this[`form_users_${this.question._id}_input`]?.$setDirty();
  }

  resetAnswers(): void {
    this.answers = this.answers.reduce((answers, answer) => {
      answers = this.sfQuestionForm.removeAnswer(answer._id, answers);
      return answers;
    }, this.answers);

    this.selectedUsersIdsSet.clear();
    this.onUsersChanges();
  }

  resetToPrefillValue(): void {
    if (this.previousAnswers && this.previousAnswers.length) {
      this.resetAnswers();

      this.previousAnswers.forEach((answer) => {
        this.answers = this.sfQuestionForm.addAnswer(
          answer.values,
          this.answers
        );

        this.selectedUsersIdsSet.add(answer.values[0].value);
      });

      this.onUsersChanges();
    }

    return this.setDirty();
  }

  getUsers(usersIds: ObjectId[]) {
    if (usersIds.length < 1) {
      return Promise.resolve([]);
    }

    const params = {
      id: usersIds,
    };

    const result = this.usersService.crud.list(params);

    return result.online ?? Promise.resolve(result.offline);
  }

  sortAnswers(): void {
    this.answers = this.answers.sort(this.sortAnswersFunction);
  }

  sortAnswersFunction(answerA: Answer, answerB: Answer): number {
    const userAFullName = `${answerA.values[1].value} ${answerA.values[2].value}`;
    const userBFullName = `${answerB.values[1].value} ${answerB.values[2].value}`;

    const comparator = new Intl.Collator(undefined, {
      usage: 'sort',
      sensitivity: 'base',
    });

    return comparator.compare(userAFullName, userBFullName);
  }
}
